import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FolderOpen, 
  BookOpen, 
  Settings, 
  Users, 
  Briefcase, 
  Brain, 
  Search,
  ChevronRight,
  ChevronDown,
  Video,
  BarChart3,
  Target,
  Library,
  HelpCircle,
  Shield,
  Zap,
  AlertCircle,
  X
} from 'lucide-react';

// Knowledge base content structure
const knowledgeBase = {
  'getting-started': {
    title: 'Getting Started',
    icon: BookOpen,
    items: [
      {
        id: 'platform-overview',
        title: 'Platform Overview',
        description: 'Complete guide to TalentCrux AI recruitment platform',
        content: {
          overview: 'TalentCrux is an AI-powered hiring platform that automates your entire recruitment pipeline, from creating a job post to making the final hiring decision. Instead of juggling separate tools for sourcing, screening, assessments, and interviews, TalentCrux brings everything into one coordinated flow so your team can move faster with more confidence.\n\nBuilt for modern recruiting teams, TalentCrux combines deep AI scoring, multi-round screening, and clear analytics into a single workspace. You define the pipeline once per role; TalentCrux runs the process, scores every candidate consistently, and surfaces the people worth your time.',
          coreComponents: [
            {
              title: 'Hiring Pipelines',
              description: 'Define a reusable, multi-stage flow for each role that ties together sourcing, screening, assessments, AI interviews, and final decisions into one coordinated pipeline.',
              features: ['Configurable stages and order', 'Score thresholds and rules', 'Per-role pipeline templates', 'Stage-level analytics']
            },
            {
              title: 'Screenings & Flows',
              description: 'Compose each pipeline from structured screening stages, including resume screening, skill assessments, and AI interviews, all driven by consistent scoring logic.',
              features: ['Resume screening', 'Skill assessments', 'AI voice interviews', 'Configurable scoring rubrics']
            },
            {
              title: 'Candidates & Profiles',
              description: 'Give every candidate a unified profile that aggregates resumes, scores, transcripts, and decisions so your team can see the full story at a glance.',
              features: ['Centralized candidate records', 'Resume and score history', 'Interview transcripts and analytics', 'Decision and status timeline']
            },
            {
              title: 'Results Hub & Analytics',
              description: 'Monitor your entire hiring funnel, compare candidates, and track key metrics like conversion rates and time-to-hire from a single analytics dashboard.',
              features: ['Pipeline funnel views', 'Candidate comparison', 'Stage-level performance metrics', 'Time-to-hire and conversion analytics']
            }
          ],
          advancedFeatures: [
            {
              title: 'Questions Library',
              description: 'Maintain a centralized repository of questions with intelligent categorization and difficulty management. Our question library ensures consistency across assessments while providing the flexibility to create role-specific evaluations.',
              capabilities: ['Unlimited questions', 'Category organization', 'Difficulty calibration', 'Usage analytics']
            },
            {
              title: 'Assessments management',
              description: 'Create comprehensive assessments and test templates that can be reused across multiple hiring processes. Build once, use everywhere to maintain evaluation standards while saving valuable time.',
              capabilities: ['Template library', 'Time controls', 'Anti-cheating measures', 'Performance tracking']
            },
            {
              title: 'Organization Management',
              description: 'Structure your recruitment teams with granular permission controls and department-based access. Ensure the right people have access to the right information while maintaining data security and compliance.',
              capabilities: ['Department hierarchy', 'User roles', 'Permission controls', 'Team collaboration']
            },
            {
              title: 'Results Analytics',
              description: 'Go beyond basic metrics with advanced analytics that provide predictive insights and optimization recommendations. Understand your hiring funnel performance and identify opportunities for improvement.',
              capabilities: ['Funnel analysis', 'Candidate comparison', 'Source effectiveness', 'Predictive insights']
            }
          ],
          technicalCapabilities: [
            {
              category: 'File Processing',
              items: ['CSV/Excel candidate imports', 'ZIP resume uploads', 'PDF document parsing', 'Multi-format support']
            },
            {
              category: 'AI Technologies',
              items: ['Natural language processing', 'Voice recognition', 'Sentiment analysis', 'Predictive analytics']
            },
            {
              category: 'Integrations',
              items: ['ATS system connections', 'Email providers', 'Calendar systems', 'API access']
            },
            {
              category: 'Security',
              items: ['Role-based permissions', 'Data encryption', 'Audit trails', 'Compliance standards']
            }
          ],
          workflowIntegration: [
            {
              stage: 'Create Job & Pipeline',
              description: 'Define your job post and attach or create a hiring pipeline that specifies which stages you will use, in what order, and with which score thresholds and weights.',
              connectedFeatures: ['Job Posts', 'Hiring Pipelines', 'Stage Configuration']
            },
            {
              stage: 'Source & Import Candidates',
              description: 'Add candidates via CSV or Excel uploads, resume uploads, and application links. TalentCrux parses resumes, builds structured profiles, and assigns each candidate to the correct job and pipeline.',
              connectedFeatures: ['Sourcing', 'Resume Parser', 'Bulk Import']
            },
            {
              stage: 'Run Multi-Round Screening',
              description: 'Guide candidates through resume screening, assessments, and AI interviews. Each stage updates candidate scores and status automatically based on your configured rules.',
              connectedFeatures: ['Resume Screening', 'Assessments', 'AI Interviews']
            },
            {
              stage: 'Review Results & Decide',
              description: 'Use the Results Hub and analytics dashboard to review ranked shortlists, drill into transcripts and scores, and move candidates to final interviews, offers, or rejection with a clear audit trail.',
              connectedFeatures: ['Results Hub', 'Analytics Dashboard', 'Decision Workflows']
            }
          ],
          platformStats: [
            { metric: 'User Roles', value: '5+', description: 'Configurable permission levels' },
            { metric: 'Assessment Types', value: '10+', description: 'Different evaluation formats' },
            { metric: 'File Formats', value: '15+', description: 'Supported import/export types' },
            { metric: 'Integration Points', value: '20+', description: 'Third-party system connections' }
          ],
          keyFeatures: [
            'End-to-end hiring pipelines that connect sourcing, screening, assessments, AI interviews, and decisions',
            'Evidence-based AI scoring across resumes, tests, and interviews using explicit rubrics and job criteria',
            'Unified candidate profiles that aggregate resumes, scores, transcripts, and decision history',
            'Multi-round screening by design (Resume → Assessment → AI Interview → Decision) as a default pattern',
            'Actionable analytics with funnel views, stage-level conversion, and time-to-hire metrics',
            'Configurable scoring weights to balance resume, assessment, and interview signals per role',
            'Support for both manual recruiter review and automated progression based on pipeline rules',
            'Robust file and data handling for large candidate imports and resume processing',
            'Role-based access and collaboration workflows for recruiting teams and stakeholders',
            'Integrations and APIs to connect hiring pipelines to your existing ATS and HR systems'
          ],
          benefits: [
            'Reduce time-to-hire by up to 70% through automation',
            'Improve candidate quality with AI-powered screening',
            'Eliminate unconscious bias with standardized evaluations',
            'Scale recruitment efforts without additional resources',
            'Make data-driven hiring decisions with comprehensive analytics',
            'Enhance collaboration with team-based workflows',
            'Ensure compliance with audit trails and permissions',
            'Streamline onboarding with integrated communication tools'
          ]
        }
      },
      {
        id: 'quick-start-guide',
        title: 'Quick Start Guide',
        description: 'Get up and running with TalentCrux in 5 simple steps',
        content: {
          overview: 'Welcome to TalentCrux! This quick start guide will help you set up your AI-powered recruitment platform and start hiring top talent efficiently. Follow these steps to configure your account, create your first job posting, and begin screening candidates.',
          prerequisites: [
            {
              item: 'Company Information',
              description: 'Have your company details, logo, and basic information ready'
            },
            {
              item: 'Team Structure',
              description: 'Know your team members and their roles in the hiring process'
            },
            {
              item: 'Job Requirements',
              description: 'Clear understanding of the positions you need to fill'
            }
          ],
          steps: [
            {
              step: 1,
              title: 'Create Your Account',
              description: 'Set up your organization profile and configure team structure. This foundation ensures proper access controls and collaboration workflows for your recruitment team.',
              details: [
                'Complete your profile with company information and branding',
                'Set up departments and team structure',
                'Configure user roles and permissions',
                'Customize organization settings and preferences'
              ],
              action: 'Go to Organization → Complete Profile Setup',
              estimatedTime: '15-30 minutes',
              tips: [
                'Add all team members who will be involved in hiring',
                'Set up proper permission levels for data security',
                'Upload your company logo for professional branding'
              ]
            },
            {
              step: 2,
              title: 'Create Your First Job Post',
              description: 'Leverage our AI Assistant to create compelling job descriptions that attract top talent. Define requirements, set screening criteria, and prepare for candidate evaluation.',
              details: [
                'Use AI Assistant to generate optimized job descriptions',
                'Define key requirements and qualifications',
                'Set salary ranges and location preferences',
                'Configure screening parameters and evaluation criteria'
              ],
              action: 'Go to Job Posts → Create Job → Use AI Assistant',
              estimatedTime: '10-20 minutes',
              tips: [
                'Be specific about required skills and experience',
                'Include company culture and benefits information',
                'Set realistic salary ranges for better candidate matching'
              ]
            },
            {
              step: 3,
              title: 'Set Up Screening Rounds',
              description: 'Configure multi-round screening process including resume screening, technical assessments, and AI interviews. Each stage is designed to progressively evaluate candidate fit.',
              details: [
                'Configure resume screening with AI-powered parsing',
                'Create or select technical assessments from question library',
                'Set up AI interview parameters and questions',
                'Define scoring criteria and evaluation weights'
              ],
              action: 'Open Job → Add Screening → Configure Multi-Round Process',
              estimatedTime: '20-40 minutes',
              tips: [
                'Start with template assessments for faster setup',
                'Customize AI interview questions for each role',
                'Test screening flow before going live'
              ]
            },
            {
              step: 4,
              title: 'Source & Invite Candidates',
              description: 'Expand your talent pool through multiple channels. Import candidate lists, share public screening links, or send personalized invitations to start the evaluation process.',
              details: [
                'Import candidate lists from CSV/Excel files',
                'Share public screening links on job boards',
                'Send personalized email invitations',
                'Monitor candidate progress and engagement'
              ],
              action: 'Go to Sourcing → Import Candidates or Share Links',
              estimatedTime: '10-30 minutes',
              tips: [
                'Clean and format your candidate lists before import',
                'Use personalized messages for better response rates',
                'Monitor candidate progress and send reminders'
              ]
            },
            {
              step: 5,
              title: 'Review Results & Hire',
              description: 'Analyze comprehensive candidate data from all screening stages. Use AI-powered insights to compare candidates and make data-driven hiring decisions.',
              details: [
                'Review candidate performance across all screening stages',
                'Compare candidates using AI-powered insights',
                'Conduct final interviews with top candidates',
                'Make hiring decisions and extend offers'
              ],
              action: 'Go to Results Analytics → Review & Compare Candidates',
              estimatedTime: '30-60 minutes',
              tips: [
                'Use the comparison tool for side-by-side evaluation',
                'Consider both technical scores and cultural fit',
                'Document decision rationale for compliance'
              ]
            }
          ],
          nextSteps: [
            {
              title: 'Explore Advanced Features',
              description: 'Learn about Questions Library, Organization Management, and advanced analytics',
              link: 'Features → Advanced Features'
            },
            {
              title: 'Configure Integrations',
              description: 'Connect with ATS systems, email providers, and calendar tools',
              link: 'Administration → Integrations'
            },
            {
              title: 'Set Up Teams & Permissions',
              description: 'Fine-tune access controls and collaboration workflows',
              link: 'Administration → Teams & Permissions'
            }
          ],
          support: [
            {
              type: 'Documentation',
              description: 'Detailed guides and API documentation',
              action: 'Browse Knowledge Base'
            },
            {
              type: 'Support Team',
              description: 'Get help from our customer success team',
              action: 'Contact Support'
            },
            {
              type: 'Video Tutorials',
              description: 'Step-by-step video guides for common tasks',
              action: 'Watch Tutorials'
            }
          ]
        }
      },
      {
        id: 'account-setup',
        title: 'Account Setup',
        description: 'Configure your TalentCrux account for optimal performance and team collaboration',
        content: {
          overview: 'Proper account setup is crucial for maximizing the effectiveness of your TalentCrux platform. This guide will walk you through configuring your organization profile, managing team access, setting up integrations, and establishing security protocols that align with your hiring workflows.',
          prerequisites: [
            {
              item: 'Admin Access',
              description: 'Ensure you have administrator privileges to configure account settings'
            },
            {
              item: 'Company Documents',
              description: 'Gather company logos, branding materials, and organizational structure'
            },
            {
              item: 'Integration Requirements',
              description: 'Identify existing ATS systems and tools that need to be connected'
            }
          ],
          sections: [
            {
              title: 'Profile Configuration',
              description: 'Set up your organization\'s foundation with comprehensive profile settings that establish your brand identity and operational parameters.',
              subsections: [
                {
                  title: 'Company Information',
                  details: [
                    'Company name, industry, and size classification',
                    'Physical locations and remote work policies',
                    'Company website and social media links',
                    'Business hours and timezone settings'
                  ],
                  action: 'Organization → Company Profile → Edit Details',
                  tips: [
                    'Complete all fields for better candidate matching',
                    'Include detailed company culture information',
                    'Set accurate timezone for scheduling features'
                  ]
                },
                {
                  title: 'Branding Settings',
                  details: [
                    'Upload company logo in high resolution',
                    'Configure brand colors and fonts',
                    'Set up email signature templates',
                    'Customize career page appearance'
                  ],
                  action: 'Organization → Branding → Configure Visual Identity',
                  tips: [
                    'Use brand-consistent colors throughout',
                    'Test email templates before sending',
                    'Ensure mobile responsiveness of branding'
                  ]
                },
                {
                  title: 'User Roles & Permissions',
                  details: [
                    'Define role hierarchy (Admin, HR Manager, Recruiter, Hiring Manager)',
                    'Configure access levels for different features',
                    'Set up approval workflows and permissions',
                    'Create custom roles for specific needs'
                  ],
                  action: 'Organization → Roles & Permissions → Configure Access',
                  tips: [
                    'Follow principle of least privilege',
                    'Regularly review and update permissions',
                    'Document role responsibilities for clarity'
                  ]
                },
                {
                  title: 'Security & Authentication',
                  details: [
                    'Enable two-factor authentication (2FA)',
                    'Configure password policies and requirements',
                    'Set up session timeout and security alerts',
                    'Configure IP whitelisting if needed'
                  ],
                  action: 'Organization → Security → Configure Authentication',
                  tips: [
                    'Enforce 2FA for all admin accounts',
                    'Use strong password requirements',
                    'Monitor login activities regularly'
                  ]
                }
              ]
            },
            {
              title: 'Team Management',
              description: 'Build your recruitment team with proper access controls and collaborative workflows that streamline your hiring process.',
              subsections: [
                {
                  title: 'Team Member Onboarding',
                  details: [
                    'Send personalized invitations to team members',
                    'Assign appropriate roles and permissions',
                    'Provide onboarding materials and training',
                    'Set up individual user preferences'
                  ],
                  action: 'Organization → Team Members → Invite New Members',
                  tips: [
                    'Use role-based invitation templates',
                    'Schedule onboarding sessions for new users',
                    'Monitor initial login and activity'
                  ]
                },
                {
                  title: 'Department Structure',
                  details: [
                    'Create departments and sub-departments',
                    'Assign department heads and managers',
                    'Configure department-specific workflows',
                    'Set up cross-department collaboration rules'
                  ],
                  action: 'Organization → Departments → Configure Structure',
                  tips: [
                    'Mirror your actual organizational structure',
                    'Keep department names consistent and clear',
                    'Review structure quarterly for updates'
                  ]
                },
                {
                  title: 'Approval Workflows',
                  details: [
                    'Define multi-level approval processes',
                    'Configure approval chains for job postings',
                    'Set up offer approval workflows',
                    'Create emergency approval procedures'
                  ],
                  action: 'Organization → Workflows → Configure Approvals',
                  tips: [
                    'Keep approval chains as short as possible',
                    'Set clear SLAs for each approval stage',
                    'Test workflows with sample requests'
                  ]
                },
                {
                  title: 'Hiring Manager Permissions',
                  details: [
                    'Define hiring manager access scope',
                    'Configure candidate viewing permissions',
                    'Set up interview scheduling rights',
                    'Configure feedback and rating permissions'
                  ],
                  action: 'Organization → Hiring Managers → Configure Permissions',
                  tips: [
                    'Limit access to relevant positions only',
                    'Provide training on feedback systems',
                    'Regularly audit permission usage'
                  ]
                }
              ]
            },
            {
              title: 'Integration Setup',
              description: 'Connect TalentCrux with your existing technology ecosystem to create a seamless hiring workflow and data synchronization.',
              subsections: [
                {
                  title: 'ATS System Integration',
                  details: [
                    'Connect with Workday, Greenhouse, Lever, or other ATS',
                    'Configure data synchronization settings',
                    'Set up bi-directional data flow',
                    'Test integration with sample data'
                  ],
                  action: 'Organization → Integrations → Connect ATS Systems',
                  tips: [
                    'Start with one-way sync initially',
                    'Test with non-production data first',
                    'Document integration rules and mappings'
                  ]
                },
                {
                  title: 'Email Configuration',
                  details: [
                    'Set up SMTP settings for email delivery',
                    'Configure email templates and variables',
                    'Set up automated email sequences',
                    'Configure bounce handling and tracking'
                  ],
                  action: 'Organization → Email → Configure Templates & Automation',
                  tips: [
                    'Use professional email signatures',
                    'Personalize templates with merge fields',
                    'Test all email flows before activation'
                  ]
                },
                {
                  title: 'Webhook Configuration',
                  details: [
                    'Set up webhook endpoints for real-time updates',
                    'Configure event triggers and payloads',
                    'Implement retry mechanisms for failed calls',
                    'Monitor webhook delivery and performance'
                  ],
                  action: 'Organization → Webhooks → Configure Endpoints',
                  tips: [
                    'Use secure webhook URLs with authentication',
                    'Implement proper error handling',
                    'Monitor webhook logs regularly'
                  ]
                },
                {
                  title: 'API Access Management',
                  details: [
                    'Generate API keys for custom integrations',
                    'Configure API rate limits and quotas',
                    'Set up API documentation and testing tools',
                    'Monitor API usage and performance'
                  ],
                  action: 'Organization → API → Manage Access Keys',
                  tips: [
                    'Rotate API keys regularly',
                    'Use environment-specific keys',
                    'Implement proper error handling in API calls'
                  ]
                }
              ]
            }
          ],
          bestPractices: [
            {
              category: 'Security',
              practices: [
                'Enable two-factor authentication for all users',
                'Regularly review and update user permissions',
                'Monitor login activities and suspicious behavior',
                'Keep software and integrations updated'
              ]
            },
            {
              category: 'Team Management',
              practices: [
                'Conduct regular team training sessions',
                'Document all processes and workflows',
                'Establish clear communication channels',
                'Set performance metrics and KPIs'
              ]
            },
            {
              category: 'Integration',
              practices: [
                'Test integrations in sandbox environment first',
                'Document all integration configurations',
                'Set up monitoring and alerting for integrations',
                'Plan for data backup and recovery'
              ]
            }
          ],
          troubleshooting: [
            {
              issue: 'Team members cannot access certain features',
              solution: 'Check role permissions and ensure proper assignment of access levels in Organization → Roles & Permissions'
            },
            {
              issue: 'Email notifications not being delivered',
              solution: 'Verify SMTP configuration in Organization → Email → Settings and check spam filters'
            },
            {
              issue: 'Integration data not syncing properly',
              solution: 'Review webhook configurations and check API key validity in Organization → Integrations'
            },
            {
              issue: 'Users experiencing login issues',
              solution: 'Reset user passwords or check 2FA settings in Organization → Security → Authentication'
            }
          ],
          nextSteps: [
            {
              title: 'Configure Job Post Templates',
              description: 'Set up reusable job posting templates for faster hiring',
              link: 'Features → Job Posts & AI Creation'
            },
            {
              title: 'Set Up Assessment Library',
              description: 'Create and organize technical assessments and questions',
              link: 'Features → Assessments'
            },
            {
              title: 'Configure Analytics Dashboard',
              description: 'Set up custom reports and metrics tracking',
              link: 'Features → Results & Analytics'
            }
          ]
        }
      }
    ]
  },
  'features': {
    title: 'Features',
    icon: Zap,
    items: [
      {
        id: 'job-posts-ai-creation',
        title: 'Job Posts & AI Creation',
        description: 'Create compelling job posts with AI assistance',
        content: {
          overview: 'Create comprehensive, market-optimized job postings using our advanced AI-powered generation system. Our platform leverages GPT-6 Luna to transform simple candidate descriptions into complete, professional job postings tailored specifically for the Indian job market with accurate salary benchmarks and requirements.',
          features: [
            'AI-powered complete job posting generation from candidate descriptions',
            'Indian market specialization with real-time salary benchmarks',
            'AI Insights panel showing how values were derived and sources used',
            '15+ automatically generated fields (title, description, salary, experience, skills, etc.)',
            'Structured JSON output with comprehensive job posting data',
            'Usage context tracking for organizations and users',
            'Support for both candidate description input and existing JD extraction',
            'Real-time generation with loading states and progress indicators'
          ],
          howItWorks: [
            'Provide a candidate description (minimum 10 characters) describing your ideal candidate',
            'AI analyzes the description using GPT-6 Luna with Indian job market expertise',
            'Generate complete structured job posting with all required fields automatically',
            'Review AI Insights panel to understand how values were derived and market sources',
            'Customize any fields as needed and publish the optimized job posting'
          ],
          technology: [
            'GPT-6 Luna model for fast and accurate generation',
            'Specialized Indian job market training and salary benchmarks',
            'Structured JSON output with 15+ required job posting fields',
            'Circuit breaker pattern for API rate limiting and error handling',
            'Usage tracking and context awareness for organizations',
            'Retry mechanisms with exponential backoff for reliability'
          ],
          generatedFields: [
            {
              field: 'Job Title & Description',
              description: 'Professional title and comprehensive job overview based on candidate requirements'
            },
            {
              field: 'Key Responsibilities',
              description: 'Detailed list of duties and responsibilities extracted from candidate profile'
            },
            {
              field: 'Experience Requirements',
              description: 'Min/max experience levels with specific requirements and qualifications'
            },
            {
              field: 'Skills & Qualifications',
              description: 'Technical and soft skills required with educational qualifications'
            },
            {
              field: 'Salary & Compensation',
              description: 'Indian market-specific salary ranges with currency and benchmarks'
            },
            {
              field: 'Location & Work Type',
              description: 'Location preferences with remote/hybrid/onsite options'
            },
            {
              field: 'Additional Requirements',
              description: 'Age preferences, gender requirements, and key attributes'
            }
          ],
          aiInsights: [
            {
              insight: 'Market Data Analysis',
              description: 'AI analyzes current Indian job market trends and salary benchmarks'
            },
            {
              insight: 'Source Attribution',
              description: 'Shows which market sources and data points influenced each generated value'
            },
            {
              insight: 'Reasoning Explanation',
              description: 'Detailed explanation of how AI arrived at specific salary ranges and requirements'
            },
            {
              insight: 'Field Confidence',
              description: 'Indicates confidence levels for different generated fields based on input quality'
            }
          ],
          bestPractices: [
            'Provide detailed candidate descriptions (minimum 10 characters) for better results',
            'Include specific skills, experience level, and role requirements in your description',
            'Review AI Insights panel to understand market data sources and reasoning',
            'Customize generated fields to match your specific company requirements and culture',
            'Use the AI Insights to justify salary ranges and requirements to stakeholders',
            'Test different candidate descriptions to see how it affects generated job postings'
          ],
          useCases: [
            {
              case: 'From Candidate Description',
              description: 'Transform ideal candidate profiles into complete job postings with all requirements'
            },
            {
              case: 'Existing JD Enhancement',
              description: 'Extract and structure information from existing job descriptions while preserving content'
            },
            {
              case: 'Market Benchmarking',
              description: 'Get accurate Indian market salary data and requirements for any role'
            },
            {
              case: 'Rapid Job Creation',
              description: 'Create multiple job postings quickly with consistent quality and market alignment'
            }
          ]
        }
      },
      {
        id: 'candidate-screening',
        title: 'Candidate Screening',
        description: 'Multi-round screening with customizable criteria',
        content: {
          overview: 'Implement comprehensive, multi-stage screening processes to identify the best candidates efficiently and consistently.',
          screeningTypes: [
            {
              type: 'Resume Screening',
              description: 'AI-powered resume analysis, skill extraction, and ranking'
            },
            {
              type: 'Assessment Screening',
              description: 'Custom tests, technical challenges, and skill evaluations'
            },
            {
              type: 'AI Interview Screening',
              description: 'Voice-based interviews with behavioral analysis'
            },
            {
              type: 'Final Review',
              description: 'Comprehensive evaluation with team collaboration'
            }
          ],
          configuration: [
            'Custom screening questions and criteria',
            'Automated scoring and ranking systems',
            'Rejection rules and communication templates',
            'Interview scheduling automation',
            'Multi-interviewer feedback collection'
          ]
        }
      },
      {
        id: 'assessments-testing',
        title: 'Assessments & Testing',
        description: 'Create custom assessments to evaluate candidate skills',
        content: {
          overview: 'Design and deploy comprehensive assessments to accurately measure candidate capabilities and fit.',
          assessmentTypes: [
            'Technical skills tests (coding, problem-solving)',
            'Cognitive ability tests (logical reasoning, aptitude)',
            'Personality and behavioral assessments',
            'Situational judgment tests',
            'Role-specific simulations and case studies'
          ],
          features: [
            'Comprehensive question bank management',
            'Time-based assessments with proctoring',
            'Anti-cheating measures and integrity monitoring',
            'Automated scoring with detailed feedback',
            'Performance analytics and benchmarking'
          ],
          creationProcess: [
            'Define assessment objectives and success criteria',
            'Select appropriate question types and difficulty levels',
            'Set time limits and proctoring rules',
            'Configure scoring rubrics and feedback',
            'Test and deploy to candidates'
          ]
        }
      },
      {
        id: 'ai-interviews',
        title: 'AI Interviews',
        description: 'Voice-based AI interviews with real-time analysis',
        content: {
          overview: 'Conduct scalable, consistent AI interviews that provide deep insights into candidate capabilities and cultural fit.',
          technology: [
            'Advanced natural language processing',
            'Real-time voice recognition and transcription',
            'Sentiment and behavioral analysis',
            'Automated personality assessment',
            'LiveKit integration for seamless audio'
          ],
          features: [
            'Multiple AI voice options and personalities',
            'Customizable interview flows and questions',
            'Real-time analysis and scoring',
            'Automated scheduling and reminders',
            'Integration with assessment results'
          ],
          setup: [
            'Create interview template with role-specific questions',
            'Configure AI personality and communication style',
            'Set evaluation criteria and scoring weights',
            'Test interview flow with sample candidates',
            'Deploy and monitor performance'
          ]
        }
      },
      {
        id: 'sourcing-candidates',
        title: 'Sourcing & Candidates',
        description: 'Advanced candidate sourcing and management',
        content: {
          overview: 'Find, engage, and manage top talent through intelligent sourcing capabilities and automated workflows.',
          sourcingChannels: [
            'Job board postings (LinkedIn, Indeed, etc.)',
            'Social media recruiting campaigns',
            'Employee referral programs',
            'Internal talent database',
            'AI-powered candidate matching'
          ],
          features: [
            'Bulk candidate import (CSV, Excel, resume ZIP/RAR archives)',
            'Advanced resume parsing and skill extraction',
            'Automated candidate scoring and ranking',
            'Communication tracking and engagement',
            'Pipeline management with visual workflows'
          ],
          workflows: [
            'Source candidates from multiple channels',
            'Parse and analyze resumes automatically',
            'Score and rank candidates based on job fit',
            'Engage with personalized outreach campaigns',
            'Track pipeline progress and conversion rates'
          ]
        }
      },
      {
        id: 'questions-libraries',
        title: 'Questions & Libraries',
        description: 'Manage question banks and test libraries',
        content: {
          overview: 'Build and maintain comprehensive question libraries to ensure consistent, high-quality assessments across your organization.',
          libraryTypes: [
            'Technical questions (coding, algorithms, system design)',
            'Behavioral questions (competency-based)',
            'Situational judgment scenarios',
            'Knowledge-based questions (industry-specific)',
            'Custom categories and tags'
          ],
          features: [
            'Intelligent question categorization and tagging',
            'Difficulty level assignment and calibration',
            'Version control and approval workflows',
            'Usage analytics and performance tracking',
            'Collaborative question creation and review'
          ],
          management: [
            'Create questions aligned with job competencies',
            'Use clear, unbiased language',
            'Include multiple difficulty levels',
            'Regular review and updates',
            'Track question performance and discrimination'
          ]
        }
      },
      {
        id: 'results-analytics',
        title: 'Results & Analytics',
        description: 'Comprehensive analytics and reporting',
        content: {
          overview: 'Gain actionable insights from recruitment data with advanced analytics and customizable reporting.',
          metrics: [
            'Time-to-hire and funnel conversion rates',
            'Cost-per-hire and ROI analysis',
            'Source effectiveness and quality scores',
            'Candidate performance and satisfaction',
            'Interviewer and team productivity'
          ],
          features: [
            'Real-time dashboards with customizable widgets',
            'Automated report generation and scheduling',
            'Trend analysis and predictive analytics',
            'Export capabilities (PDF, Excel, API)',
            'Integration with BI tools (Tableau, Power BI)'
          ],
          reports: [
            'Hiring funnel analysis and bottleneck identification',
            'Candidate performance and fit reports',
            'Source effectiveness and channel optimization',
            'Team productivity and collaboration metrics',
            'Diversity and inclusion analytics'
          ]
        }
      }
    ]
  },
  'administration': {
    title: 'Administration',
    icon: Settings,
    items: [
      {
        id: 'teams-permissions',
        title: 'Teams & Permissions',
        description: 'Manage team structure and access controls',
        content: {
          overview: 'Configure team structure and granular permissions to ensure proper access control and collaboration.',
          roles: [
            {
              role: 'Organization Admin',
              permissions: 'Full access to all features, billing, and settings'
            },
            {
              role: 'Department Admin',
              permissions: 'Manage department teams, job postings, and screenings'
            },
            {
              role: 'Recruiter',
              permissions: 'Create jobs, screen candidates, conduct interviews'
            },
            {
              role: 'Interviewer',
              permissions: 'Conduct interviews and provide feedback only'
            },
            {
              role: 'Viewer',
              permissions: 'Read-only access to reports and analytics'
            }
          ],
          features: [
            'Custom role creation with granular controls',
            'Department-based access restrictions',
            'Approval workflows for critical actions',
            'Comprehensive audit logs and activity tracking',
            'Temporary access and delegation options'
          ]
        }
      },
      {
        id: 'settings-configuration',
        title: 'Settings & Configuration',
        description: 'Platform settings and customization options',
        content: {
          overview: 'Customize platform settings to match your organization\'s specific needs and workflows.',
          categories: [
            {
              category: 'General Settings',
              items: [
                'Company information and branding',
                'Time zone and language preferences',
                'Email notification templates',
                'Communication preferences'
              ]
            },
            {
              category: 'Security Settings',
              items: [
                'Password policies and requirements',
                'Two-factor authentication enforcement',
                'Session management and timeout controls',
                'API key management and rotation'
              ]
            },
            {
              category: 'Integration Settings',
              items: [
                'ATS system connections and sync settings',
                'Email service provider configuration',
                'Webhook endpoints and event subscriptions',
                'Custom API integrations and authentication'
              ]
            }
          ]
        }
      },
      {
        id: 'integrations',
        title: 'Integrations',
        description: 'Connect with your existing tools and systems',
        content: {
          overview: 'Seamlessly connect TalentCrux with your existing recruitment ecosystem for streamlined workflows.',
          categories: [
            {
              category: 'ATS Integrations',
              systems: [
                'Workday, Greenhouse, Lever, SmartRecruiters',
                'Custom API connections for proprietary systems',
                'Bi-directional data synchronization',
                'Real-time status updates and notifications'
              ]
            },
            {
              category: 'Communication Tools',
              systems: [
                'Email providers (Gmail, Outlook, SendGrid)',
                'Slack and Microsoft Teams integration',
                'Calendar systems for interview scheduling',
                'SMS and messaging platforms'
              ]
            },
            {
              category: 'Analytics & BI Tools',
              systems: [
                'Google Analytics for recruitment marketing',
                'Tableau and Power BI dashboards',
                'Custom data warehouse connections',
                'Real-time data streaming and alerts'
              ]
            }
          ],
          benefits: [
            'Eliminate duplicate data entry',
            'Automate routine tasks and communications',
            'Improve data accuracy and consistency',
            'Enable comprehensive reporting across systems'
          ]
        }
      },
      {
        id: 'help-support',
        title: 'Help & Support',
        description: 'Get help and find answers to your questions',
        content: {
          overview: 'Access comprehensive support resources and get assistance when you need it.',
          resources: [
            {
              resource: 'Knowledge Base',
              description: 'Comprehensive guides and documentation'
            },
            {
              resource: 'FAQ Section',
              description: 'Quick answers to common questions'
            },
            {
              resource: 'Support Tickets',
              description: 'Direct assistance from our support team'
            },
            {
              resource: 'Video Tutorials',
              description: 'Step-by-step visual guides'
            },
            {
              resource: 'Community Forum',
              description: 'Connect with other users and best practices'
            }
          ],
          contactMethods: [
            '24/7 email support with guaranteed response times',
            'Live chat for immediate assistance',
            'Phone support for enterprise customers',
            'Priority support with dedicated account managers'
          ]
        }
      }
    ]
  }
};

const Resources = () => {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [apiVideos, setApiVideos] = useState([]);
  const [apiVideosLoading, setApiVideosLoading] = useState(true);
  const [videoModalLesson, setVideoModalLesson] = useState(null);
  const [videoModalClosing, setVideoModalClosing] = useState(false);

  const closeVideoModal = () => {
    if (videoModalClosing) return;
    setVideoModalClosing(true);
    // Unmount after a short delay so any in-flight play() resolves before
    // ReactPlayer's cleanup calls pause() (avoids AbortError).
    setTimeout(() => {
      setVideoModalLesson(null);
      setVideoModalClosing(false);
    }, 400);
  };

  useEffect(() => {
    if (!videoModalLesson) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') closeVideoModal();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [videoModalLesson]);

  // Swallow the play()/pause() AbortError so it doesn't show as uncaught
  useEffect(() => {
    const handler = (e) => {
      if (e?.reason?.name === 'AbortError' && typeof e.reason?.message === 'string' && e.reason.message.includes('play()')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/resources/videos')
      .then((res) => {
        if (!cancelled) {
          const list = res.data?.videos;
          setApiVideos(Array.isArray(list) ? list : []);
        }
      })
      .catch(() => {
        if (!cancelled) setApiVideos([]);
      })
      .finally(() => {
        if (!cancelled) setApiVideosLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const categories = [
    {
      id: 'getting-started',
      label: 'Start here',
      description: 'Perfect for new users. Short videos to get your first job live.',
      icon: BookOpen,
    },
    {
      id: 'job-posts',
      label: 'Job posts & screenings',
      description: 'Walkthroughs on creating roles, screenings and AI flows.',
      icon: Briefcase,
    },
    {
      id: 'results',
      label: 'Results & analytics',
      description: 'Understand scores, shortlists and how AI is deciding.',
      icon: BarChart3,
    },
    {
      id: 'teams',
      label: 'Teams & access',
      description: 'Invite your team, manage permissions and org structure.',
      icon: Users,
    },
    {
      id: 'library',
      label: 'Library',
      description: 'Assessment and interview library walkthroughs and how to use your content libraries.',
      icon: Library,
    },
    {
      id: 'support-requests',
      label: 'Support requests',
      description: 'Help content for raising and managing support requests.',
      icon: HelpCircle,
    },
  ];

  const upcomingLessons = [
    {
      id: 'overview',
      title: 'Platform overview in 7 minutes',
      length: '7 min',
      level: 'Beginner',
      category: 'getting-started',
    },
    {
      id: 'first-job',
      title: 'Create your first job & pipeline',
      length: '10 min',
      level: 'Beginner',
      category: 'job-posts',
    },
    {
      id: 'screening-flow',
      title: 'Run a complete screening flow',
      length: '12 min',
      level: 'Intermediate',
      category: 'job-posts',
    },
    {
      id: 'results-tour',
      title: 'Reading scores & shortlists',
      length: '8 min',
      level: 'Beginner',
      category: 'results',
    },
    {
      id: 'ai-interview-setup',
      title: 'Set up your first AI interview',
      length: '9 min',
      level: 'Intermediate',
      category: 'job-posts',
    },
    {
      id: 'invite-candidates',
      title: 'Invite candidates and send links',
      length: '6 min',
      level: 'Beginner',
      category: 'getting-started',
    },
    {
      id: 'bulk-import',
      title: 'Import candidates from Excel or CSV',
      length: '8 min',
      level: 'Beginner',
      category: 'getting-started',
    },
    {
      id: 'shortlist-flow',
      title: 'Shortlist and move candidates to next stage',
      length: '7 min',
      level: 'Intermediate',
      category: 'results',
    },
    {
      id: 'team-permissions',
      title: 'Add teammates and set permissions',
      length: '5 min',
      level: 'Beginner',
      category: 'teams',
    },
    {
      id: 'notifications',
      title: 'Control notifications and updates',
      length: '4 min',
      level: 'Beginner',
      category: 'teams',
    },
    {
      id: 'reporting',
      title: 'Download and share hiring reports',
      length: '9 min',
      level: 'Intermediate',
      category: 'results',
    },
    {
      id: 'troubleshooting',
      title: 'Common issues and quick fixes',
      length: '6 min',
      level: 'Beginner',
      category: 'getting-started',
    },
  ];

  const lessonsFromApi = apiVideos.map((v) => ({
    id: v._id,
    title: v.title || 'Untitled',
    length: v.minutes != null ? `${v.minutes} min` : '—',
    level: v.level || 'Beginner',
    category: v.category || 'getting-started',
    thumbnailUrl: v.thumbnailUrl,
    videoUrl: v.videoUrl,
  }));
  const allLessons = lessonsFromApi;

  let filteredLessons = allLessons.filter(
    (lesson) => activeCategory === 'getting-started' || lesson.category === activeCategory,
  );

  if (searchTerm.trim()) {
    const q = searchTerm.trim().toLowerCase();
    filteredLessons = filteredLessons.filter((lesson) =>
      lesson.title.toLowerCase().includes(q),
    );
  }

  const primaryLessons = filteredLessons.slice(0, 6);
  const extraLessons = filteredLessons.slice(6);
  const visibleLessons = showAll ? filteredLessons : primaryLessons;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="px-4 sm:px-8 lg:px-12 py-10">
        {/* Header / Hero */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
              <FolderOpen className="h-4 w-4 mr-1.5" />
              Resources
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Learn how to use TalentCrux
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl">
              Watch short videos on creating jobs, running screenings and understanding results. Pick
              a card, press play and follow along in your own account.
            </p>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((category) => {
            const Icon = category.icon;
            const isActive = category.id === activeCategory;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => setActiveCategory(category.id)}
                className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs sm:text-sm transition-colors ${
                  isActive
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700'
                }`}
              >
                <Icon className="h-4 w-4 mr-1.5" />
                {category.label}
              </button>
            );
          })}
        </div>

        {/* Video lessons grid */}
        <div className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Video lessons</h2>
              <span className="text-xs text-slate-500">
                {filteredLessons.length} lesson
                {filteredLessons.length !== 1 ? 's' : ''} · watch and follow along
              </span>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowAll(false);
                }}
                placeholder="Search lessons..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-xs sm:text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
            </div>
          </div>

          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              showAll ? 'max-h-[2000px]' : 'max-h-[650px]'
            }`}
          >
            {apiVideosLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex flex-col rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden animate-pulse">
                    <div className="aspect-video bg-slate-200" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 w-20 rounded bg-slate-200" />
                      <div className="h-4 w-full rounded bg-slate-200" />
                      <div className="h-4 w-3/4 rounded bg-slate-200" />
                      <div className="flex justify-between">
                        <div className="h-3 w-12 rounded bg-slate-200" />
                        <div className="h-3 w-16 rounded bg-slate-200" />
                      </div>
                      <div className="h-3 w-20 rounded bg-slate-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredLessons.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">
                No video lessons yet. Check back soon.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visibleLessons.map((lesson) => (
                  <button
                    key={lesson.id}
                    type="button"
                    onClick={() => lesson.videoUrl && setVideoModalLesson(lesson)}
                    className="group flex flex-col rounded-xl bg-white border border-slate-200 shadow-sm hover:border-blue-500 hover:shadow-md hover:-translate-y-0.5 transition-all text-left overflow-hidden"
                  >
                    <div className="aspect-video bg-slate-900/90 flex items-center justify-center relative overflow-hidden">
                      {lesson.thumbnailUrl ? (
                        <img
                          src={lesson.thumbnailUrl}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : null}
                      <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/30 via-transparent to-indigo-500/40 group-hover:scale-105 transition-transform" />
                      <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white/90 group-hover:bg-blue-50 transition-colors">
                        <Video className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="p-3 space-y-1.5">
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        {(lesson.category || '').replace(/-/g, ' ')}
                      </p>
                      <p className="text-sm font-semibold text-slate-900 line-clamp-2">
                        {lesson.title}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{lesson.length}</span>
                        <span>{lesson.level}</span>
                      </div>
                      <p className="text-[11px] text-emerald-600 font-medium">
                        {lesson.videoUrl ? 'Watch video' : 'Video coming soon'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {!apiVideosLoading && extraLessons.length > 0 && (
            <div className="flex justify-center mt-3">
              <button
                type="button"
                onClick={() => setShowAll((prev) => !prev)}
                className="inline-flex items-center px-4 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:border-blue-500 hover:text-blue-700 transition-colors"
              >
                {showAll ? 'Show fewer lessons' : `Show ${extraLessons.length} more lessons`}
                <ChevronDown
                  className={`ml-1.5 h-3.5 w-3.5 transition-transform ${
                    showAll ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Video player modal */}
      {videoModalLesson && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 transition-opacity duration-200 ${videoModalClosing ? 'opacity-0 pointer-events-none' : ''}`}
          onClick={closeVideoModal}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] rounded-xl bg-white shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeVideoModal}
              className="absolute top-3 right-3 z-10 rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col overflow-auto">
              {/* Header: title + meta */}
              <div className="px-6 pt-5 pb-3 pr-12 border-b border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900">
                  {videoModalLesson.title}
                </h2>
                <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                  <span>{videoModalLesson.length}</span>
                  <span>{videoModalLesson.level}</span>
                  <span className="capitalize">{(videoModalLesson.category || '').replace(/-/g, ' ')}</span>
                </div>
              </div>

              {/* Video player */}
              <div className="p-4 bg-slate-900 rounded-b-xl">
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black min-h-[280px]">
              <video
                key={videoModalLesson.videoUrl}
                src={videoModalLesson.videoUrl}
                controls
                controlsList="nodownload noplaybackrate"
                onContextMenu={(e) => e.preventDefault()}
                className="absolute inset-0 h-full w-full rounded-lg bg-black"
                playsInline
              >
                Your browser does not support the video tag.
              </video>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Content rendering helper function
function renderContent(content) {
  if (!content) return null;

  // Special layout for the Platform Overview page – article-style like Ribbon's overview
  const isPlatformOverview =
    !!content.overview &&
    Array.isArray(content.coreComponents) &&
    Array.isArray(content.workflowIntegration) &&
    Array.isArray(content.keyFeatures);

  // Special layout for the Quick Start Guide page – clean, linear quick-start doc
  const isQuickStartGuide =
    !!content.overview &&
    Array.isArray(content.steps) &&
    !Array.isArray(content.sections);

  if (isPlatformOverview) {
    return (
      <div className="space-y-10">
        {/* Intro text */}
        <section>
          <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
            {content.overview}
          </p>
        </section>

        {/* Key Concepts (Core Components) */}
        {content.coreComponents && (
          <section className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">Key Concepts</h3>
            {content.coreComponents.map((component, index) => (
              <div key={index} className="space-y-2">
                <h4 className="text-lg font-semibold text-gray-900">
                  {component.title}
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {component.description}
                </p>
                {component.features && component.features.length > 0 && (
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    {component.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>{feature}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Getting Started (Pipeline flow) */}
        {content.workflowIntegration && (
          <section className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              Getting Started with Your Hiring Pipeline
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              {content.workflowIntegration.map((workflow, index) => (
                <li key={index}>
                  <span className="font-semibold text-gray-900">{workflow.stage}.</span>{' '}
                  <span>{workflow.description}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* What Makes TalentCrux Different */}
        {content.keyFeatures && (
          <section className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              What Makes TalentCrux Different
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              {content.keyFeatures.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  if (isQuickStartGuide) {
    return (
      <div className="space-y-10">
        {/* Overview */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Quick Start Overview</h3>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {content.overview}
          </p>
        </section>

        {/* Prerequisites */}
        {content.prerequisites && (
          <section className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">Before You Start</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              {content.prerequisites.map((item, index) => (
                <li key={index}>
                  <span className="font-semibold text-gray-900">{item.item}:</span>{' '}
                  <span>{item.description}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Step-by-step guide */}
        {content.steps && (
          <section className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">Quick Start Steps</h3>
            <ol className="space-y-4 list-decimal list-inside text-sm text-gray-700">
              {content.steps.map((step, index) => (
                <li key={index} className="space-y-2">
                  <h4 className="font-semibold text-gray-900">
                    Step {step.step}: {step.title}
                  </h4>
                  <p className="leading-relaxed">{step.description}</p>
                  {step.details && step.details.length > 0 && (
                    <div>
                      <p className="font-semibold text-gray-900 text-xs mb-1">What you'll do:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {step.details.map((detail, i) => (
                          <li key={i}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {step.tips && step.tips.length > 0 && (
                    <div>
                      <p className="font-semibold text-gray-900 text-xs mb-1">Pro tips:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {step.tips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {step.action && (
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold">In the app:</span> {step.action}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Next steps */}
        {content.nextSteps && (
          <section className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">Next Steps</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              {content.nextSteps.map((item, index) => (
                <li key={index}>
                  <span className="font-semibold text-gray-900">{item.title}:</span>{' '}
                  <span>{item.description}</span>{' '}
                  <span className="text-gray-500">({item.link})</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Support */}
        {content.support && (
          <section className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">Where to Get Help</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              {content.support.map((item, index) => (
                <li key={index}>
                  <span className="font-semibold text-gray-900">{item.type}:</span>{' '}
                  <span>{item.description}</span>{' '}
                  <span className="text-gray-500">({item.action})</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  if (content.overview) {
    return (
      <div className="space-y-8">
        {content.overview && (
          <div className="bg-electric-blue-50 border border-electric-blue-200 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-electric-blue-900 mb-4">Overview</h3>
            <p className="text-electric-blue-800 text-base leading-relaxed">{content.overview}</p>
          </div>
        )}
        
        {content.coreComponents && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Core Platform Components</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {content.coreComponents && content.coreComponents.map((component, index) => (
                <div key={index} className="bg-gradient-to-br from-electric-blue-50 to-primary-100 border border-electric-blue-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-electric-blue-600 rounded-xl flex items-center justify-center mr-4">
                      <Briefcase className="h-7 w-7 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">{component.title}</h4>
                  </div>
                  <p className="text-gray-700 mb-6 text-base leading-relaxed">{component.description}</p>
                  <ul className="space-y-3">
                    {component.features && component.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <div className="w-2 h-2 bg-electric-blue-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.advancedFeatures && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Advanced Features</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {content.advancedFeatures && content.advancedFeatures.map((feature, index) => (
                <div key={index} className="bg-gradient-to-br from-lavender-purple-50 to-lavender-purple-100 border border-lavender-purple-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-lavender-purple-600 rounded-xl flex items-center justify-center mr-4">
                      <Brain className="h-7 w-7 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">{feature.title}</h4>
                  </div>
                  <p className="text-gray-700 mb-6 text-base leading-relaxed">{feature.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {feature.capabilities && feature.capabilities.map((capability, capIndex) => (
                      <div key={capIndex} className="bg-white bg-opacity-60 rounded-lg px-3 py-2">
                        <p className="text-sm text-lavender-purple-800">{capability}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.technicalCapabilities && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Technical Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.technicalCapabilities && content.technicalCapabilities.map((tech, index) => (
                <div key={index} className="bg-gradient-to-br from-success-50 to-success-100 border border-success-200 rounded-xl p-6">
                  <h4 className="text-base font-semibold text-success-900 mb-4">{tech.category}</h4>
                  <ul className="space-y-2">
                    {tech.items && tech.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center text-sm text-success-800">
                        <div className="w-1.5 h-1.5 bg-success-600 rounded-full mr-3"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.workflowIntegration && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Workflow Integration</h3>
            <div className="space-y-6">
              {content.workflowIntegration && content.workflowIntegration.map((workflow, index) => (
                <div key={index} className="flex space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-warning-500 to-warning-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1 bg-gradient-to-r from-warning-50 to-warning-100 border border-warning-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">{workflow.stage}</h4>
                    <p className="text-gray-700 text-base mb-4 leading-relaxed">{workflow.description}</p>
                    <div className="flex flex-wrap gap-3">
                      {workflow.connectedFeatures && workflow.connectedFeatures.map((feature, featureIndex) => (
                        <span key={featureIndex} className="bg-white bg-opacity-70 text-sm text-warning-800 px-3 py-2 rounded-lg">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.platformStats && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Platform Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {content.platformStats && content.platformStats.map((stat, index) => (
                <div key={index} className="bg-gradient-to-br from-electric-blue-600 to-lavender-purple-600 rounded-xl p-6 text-white text-center">
                  <div className="text-3xl font-bold mb-2">{stat.value}</div>
                  <div className="text-base font-medium mb-2">{stat.metric}</div>
                  <div className="text-sm opacity-90">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.keyFeatures && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Key Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.keyFeatures && content.keyFeatures.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4 bg-secondary-50 rounded-xl p-4">
                  <div className="w-2.5 h-2.5 bg-electric-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700 text-base">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.benefits && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Platform Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.benefits && content.benefits.map((benefit, index) => (
                <div key={index} className="bg-gradient-to-r from-success-50 to-success-100 border border-success-200 rounded-xl p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 bg-success-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-success-800 font-medium text-base">{benefit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.steps && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Getting Started Steps</h3>
            <div className="space-y-8">
              {content.steps && content.steps.map((step, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-electric-blue-600 to-lavender-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-2xl font-semibold text-gray-900">{step.title}</h4>
                        <span className="bg-electric-blue-100 text-electric-blue-800 px-3 py-1 rounded-lg text-sm font-medium">
                          {step.estimatedTime}
                        </span>
                      </div>
                      <p className="text-gray-700 text-base mb-6 leading-relaxed">{step.description}</p>
                      
                      <div className="mb-6">
                        <h5 className="text-lg font-semibold text-gray-900 mb-3">What You'll Do:</h5>
                        <ul className="space-y-2">
                          {step.details && step.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-electric-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-sm">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mb-6">
                        <h5 className="text-lg font-semibold text-gray-900 mb-3">Pro Tips:</h5>
                        <div className="space-y-2">
                          {step.tips && step.tips.map((tip, tipIndex) => (
                            <div key={tipIndex} className="bg-success-50 border border-success-200 rounded-lg p-3">
                              <div className="flex items-start space-x-2">
                                <div className="w-5 h-5 bg-success-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className="text-success-800 text-sm">{tip}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-electric-blue-50 border border-electric-blue-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-5 h-5 bg-electric-blue-600 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-electric-blue-800 font-medium">{step.action}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.nextSteps && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.nextSteps.map((nextStep, index) => (
                <div key={index} className="bg-gradient-to-br from-lavender-purple-50 to-lavender-purple-100 border border-lavender-purple-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h4 className="text-lg font-semibold text-lavender-purple-900 mb-3">{nextStep.title}</h4>
                  <p className="text-lavender-purple-800 text-sm mb-4">{nextStep.description}</p>
                  <div className="bg-white bg-opacity-60 rounded-lg px-3 py-2">
                    <p className="text-lavender-purple-700 text-sm font-medium">{nextStep.link}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.support && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Get Help & Support</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.support && content.support.map((supportItem, index) => (
                <div key={index} className="bg-gradient-to-br from-secondary-50 to-secondary-100 border border-secondary-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-secondary-600 rounded-lg flex items-center justify-center mr-3">
                      <HelpCircle className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-secondary-900">{supportItem.type}</h4>
                  </div>
                  <p className="text-secondary-800 text-sm mb-4">{supportItem.description}</p>
                  <div className="bg-white bg-opacity-60 rounded-lg px-3 py-2">
                    <p className="text-secondary-700 text-sm font-medium">{supportItem.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.prerequisites && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Before You Start</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.prerequisites.map((prereq, index) => (
                <div key={index} className="bg-gradient-to-br from-warning-50 to-warning-100 border border-warning-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-warning-900 mb-3">{prereq.item}</h4>
                  <p className="text-warning-800 text-sm">{prereq.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.overview && (
          <div className="bg-electric-blue-50 border border-electric-blue-200 rounded-xl p-8 mb-8">
            <h3 className="text-xl font-semibold text-electric-blue-900 mb-4">Overview</h3>
            <p className="text-electric-blue-800 text-base leading-relaxed">{content.overview}</p>
          </div>
        )}
        
        {content.prerequisites && (
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Prerequisites</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.prerequisites.map((prereq, index) => (
                <div key={index} className="bg-gradient-to-br from-warning-50 to-warning-100 border border-warning-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-warning-900 mb-3">{prereq.item}</h4>
                  <p className="text-warning-800 text-sm">{prereq.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.sections && (
          <div className="space-y-12">
            {content.sections && content.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">{section.title}</h3>
                  <p className="text-gray-700 text-base leading-relaxed">{section.description}</p>
                </div>
                
                <div className="space-y-8">
                  {section.subsections && section.subsections.map((subsection, subIndex) => (
                    <div key={subIndex} className="bg-gradient-to-br from-secondary-50 to-secondary-100 border border-secondary-200 rounded-xl p-6">
                      <h4 className="text-xl font-semibold text-secondary-900 mb-4">{subsection.title}</h4>
                      
                      <div className="mb-6">
                        <h5 className="text-lg font-semibold text-gray-900 mb-3">Configuration Details:</h5>
                        <ul className="space-y-2">
                          {subsection.details && subsection.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-electric-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-sm">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mb-6">
                        <div className="bg-electric-blue-50 border border-electric-blue-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 bg-electric-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-electric-blue-800 font-medium">{subsection.action}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="text-lg font-semibold text-gray-900 mb-3">Pro Tips:</h5>
                        <div className="space-y-2">
                          {subsection.tips && subsection.tips.map((tip, tipIndex) => (
                            <div key={tipIndex} className="bg-success-50 border border-success-200 rounded-lg p-3">
                              <div className="flex items-start space-x-2">
                                <div className="w-5 h-5 bg-success-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className="text-success-800 text-sm">{tip}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {content.bestPractices && (
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Best Practices</h3>
            <div className="space-y-6">
              {content.bestPractices && content.bestPractices.map((category, index) => (
                <div key={index} className="bg-gradient-to-br from-lavender-purple-50 to-lavender-purple-100 border border-lavender-purple-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-lavender-purple-900 mb-4">{category.category}</h4>
                  <ul className="space-y-2">
                    {category.practices && category.practices.map((practice, practiceIndex) => (
                      <li key={practiceIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-lavender-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-lavender-purple-800 text-sm">{practice}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.troubleshooting && (
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Troubleshooting</h3>
            <div className="space-y-4">
              {content.troubleshooting.map((item, index) => (
                <div key={index} className="bg-gradient-to-r from-error-50 to-error-100 border border-error-200 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-error-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-error-900 mb-2">{item.issue}</h4>
                      <p className="text-error-800 text-sm">{item.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.nextSteps && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.nextSteps.map((nextStep, index) => (
                <div key={index} className="bg-gradient-to-br from-success-50 to-success-100 border border-success-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h4 className="text-lg font-semibold text-success-900 mb-3">{nextStep.title}</h4>
                  <p className="text-success-800 text-sm mb-4">{nextStep.description}</p>
                  <div className="bg-white bg-opacity-60 rounded-lg px-3 py-2">
                    <p className="text-success-700 text-sm font-medium">{nextStep.link}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.technology && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Technology Stack</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.technology.map((tech, index) => (
                <div key={index} className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-4">
                  <p className="text-indigo-800 text-sm">{tech}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.generatedFields && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Generated Fields</h3>
            <div className="space-y-4">
              {content.generatedFields.map((field, index) => (
                <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <div className="w-4 h-4 bg-white rounded-sm"></div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-blue-900 mb-2">{field.field}</h4>
                      <p className="text-blue-800 text-sm">{field.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.aiInsights && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">AI Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.aiInsights.map((insight, index) => (
                <div key={index} className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <Brain className="h-4 w-4 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-purple-900">{insight.insight}</h4>
                  </div>
                  <p className="text-purple-800 text-sm">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.useCases && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Use Cases</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.useCases.map((useCase, index) => (
                <div key={index} className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-green-900 mb-3">{useCase.case}</h4>
                  <p className="text-green-800 text-sm">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.features && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.features && content.features.map((feature, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-700">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.howItWorks && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h3>
            <div className="space-y-3">
              {content.howItWorks && content.howItWorks.map((step, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <span className="text-gray-700">{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.screeningTypes && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Screening Types</h3>
            <div className="space-y-4">
              {content.screeningTypes.map((type, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{type.type}</h4>
                  <p className="text-gray-600">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.assessmentTypes && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Assessment Types</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.assessmentTypes.map((type, index) => (
                <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-purple-800">{type}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.technology && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Technology</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.technology.map((tech, index) => (
                <div key={index} className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-indigo-800">{tech}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.sourcingChannels && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sourcing Channels</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.sourcingChannels.map((channel, index) => (
                <div key={index} className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-teal-800">{channel}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.libraryTypes && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Library Types</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.libraryTypes.map((type, index) => (
                <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800">{type}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.metrics && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.metrics.map((metric, index) => (
                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">{metric}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.roles && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">User Roles</h3>
            <div className="space-y-4">
              {content.roles.map((role, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{role.role}</h4>
                  <p className="text-gray-600">{role.permissions}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.categories && (
          <div>
            {content.categories.map((category, index) => (
              <div key={index} className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.category}</h3>
                {category.items && (
                  <ul className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {category.systems && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.systems.map((system, systemIndex) => (
                      <div key={systemIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-gray-700">{system}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {content.resources && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Resources</h3>
            <div className="space-y-4">
              {content.resources.map((resource, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{resource.resource}</h4>
                  <p className="text-gray-600">{resource.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.workflows && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Workflow Process</h3>
            <div className="space-y-3">
              {content.workflows.map((workflow, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <span className="text-gray-700">{workflow}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.creationProcess && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Creation Process</h3>
            <div className="space-y-3">
              {content.creationProcess.map((process, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <span className="text-gray-700">{process}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.setup && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Setup Process</h3>
            <div className="space-y-3">
              {content.setup.map((setupItem, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <span className="text-gray-700">{setupItem}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.management && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Management Best Practices</h3>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <ul className="space-y-2">
                {content.management.map((practice, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-orange-800">{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {content.reports && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Available Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.reports.map((report, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-700">{report}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.benefits && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Integration Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.benefits.map((benefit, index) => (
                <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {content.contactMethods && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Methods</h3>
            <div className="space-y-3">
              {content.contactMethods.map((method, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">{method}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return <div>No content available</div>;
}

export default Resources;
